import { CLOUD, loggedIn } from '../cloud/api.js';
import { addDays, loc, today } from '../dates.js';
import { t } from '../i18n/index.js';
import { eventsIn, stu } from '../model.js';
import { S, persist } from '../state.js';
import { IC, RT, esc, render, toast } from '../ui.js';
import { openFeedSheet } from './feed.js';
import { installApp, showInstall } from './install.js';
import { PUSH, disablePush, enablePush, iosNeedsInstall, isMobile, pushConfig } from './push.js';

/* Mirrors NDEF in supabase/functions/notify — the server applies these same defaults. */
export const NDEF={push:true,email:true,prep:true,prepDays:3,soon:true,soonMin:60,after:true,agenda:false,agendaAt:'07:30',quiet:true,quietFrom:'22:00',quietTo:'07:00'};
export const nprefs=()=>({...NDEF,...(S.settings.notify||{})});
export const evStart=e=>new Date(e.date+'T'+(e.time||'00:00')).getTime();

/** this device: unknown | on | off | denied | unsupported | ios-install */
export function deviceState(){
  if(!PUSH.ready)return 'unknown';
  if(iosNeedsInstall())return 'ios-install';
  if(!PUSH.supported)return 'unsupported';
  if(PUSH.sub)return 'on';
  if(PUSH.perm==='denied')return 'denied';
  return 'off';
}

/** The next reminders the server will send, worked out the same way (device time zone). */
export function upcomingReminders(n){
  const p=nprefs();const now=Date.now();const out=[];
  for(const e of eventsIn(today(),addDays(today(),(+p.prepDays||3)+8))){
    if(e.status!=='planned')continue;
    const s=stu(e.studentId);if(!s||s.archived)continue;
    const start=evStart(e);
    if(p.prep){const at=start-(+p.prepDays||3)*864e5;if(at>now)out.push({at,kind:'prep',e,s})}
    if(p.soon){const at=start-(+p.soonMin||60)*6e4;if(at>now)out.push({at,kind:'soon',e,s})}
  }
  out.sort((a,b)=>a.at-b.at);
  return out.slice(0,n||3);
}
const fmtAt=ms=>{
  const d=new Date(ms),td=today(),ds=d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
  const time=d.toLocaleTimeString(loc(),{hour:'2-digit',minute:'2-digit'});
  if(ds===td)return t('c.today')+', '+time;
  if(ds===addDays(td,1))return t('ov.tomorrowCap')+', '+time;
  return d.toLocaleDateString(loc(),{weekday:'short',day:'numeric',month:'short'})+', '+time;
};
const FEATHER='<svg viewBox="96 96 320 340" aria-hidden="true"><path d="M352 112c-72 8-136 56-168 120-20.8 41.6-28.8 88-31.2 132.8l-4.8 39.2c-.8 7.2 7.2 12.8 13.6 8.8l33.6-20.8C240 368 284 344 316 308c44-48 56-128 36-196z" fill="#F7F6F1"/><path d="M160 392 336 160" stroke="#E0342B" stroke-width="19" stroke-linecap="round"/></svg>';
export const FEATHER_ICON=FEATHER;

/* ---------------- home ---------------- */
/** Install / switch-on prompt — sits right under the hero so phones see it first. */
export function homeReminderCta(){
  if(!loggedIn())return '';
  const st=deviceState();const p=nprefs();
  const dismissed=S.settings.dismissed||{};
  if(showInstall()&&!dismissed.install){
    return `<div class="card mb ncard install"><div class="ncard-ic">${FEATHER}</div><div class="grow">
      <h3>${t('inst.title')}</h3><p class="muted">${t('inst.body')}</p>
      <div class="row mt"><button class="btn primary" data-inst="go">${t('inst.btn')}</button><button class="btn ghost small" data-dismiss="install">${t('inst.later')}</button></div></div></div>`;
  }
  if(!isMobile()&&st!=='on'&&!dismissed.pushask){
    const here=PUSH.supported&&PUSH.perm!=='denied'?`<button class="btn small" data-push="on">${t('n.qrHere')}</button>`:'';
    return `<div class="card mb ncard install qr"><div class="ncard-ic">${FEATHER}</div><div class="grow">
      <h3>${t('n.qrTitle')}</h3><p class="muted">${t('n.qrBody')}</p>
      <div class="row mt">${here}<button class="btn ghost small" data-dismiss="pushask">${t('inst.later')}</button></div></div>
      <img class="qrimg" data-qr="${esc(location.origin+'/')}" alt=""></div>`;
  }
  if(st==='off'&&!dismissed.pushask){
    return `<div class="card mb ncard install"><div class="ncard-ic">${FEATHER}</div><div class="grow">
      <h3>${t('n.homeOff')}</h3><p class="muted">${t('n.homeOffBody',{d:p.prepDays,m:p.soonMin})}</p>
      <div class="row mt"><button class="btn primary" data-push="on" ${PUSH.busy?'disabled':''}>${t('n.enable')}</button><button class="btn ghost small" data-dismiss="pushask">${t('inst.later')}</button></div></div></div>`;
  }
  return '';
}
export function homeRemindersCard(){
  if(!loggedIn())return '';
  const st=deviceState();const p=nprefs();
  const list=upcomingReminders(3);
  const what=r=>r.kind==='prep'?t('n.rPrep',{name:esc(r.s.first)}):t('n.rSoon',{name:esc(r.s.first),n:p.soonMin});
  return `<div class="card mb ncard">
    <div class="cardhead"><h2>${t('n.title')}</h2><button class="btn small ghost" data-set="reminders">${t('n.settings')}</button></div>
    ${list.length?'<ul class="nlist">'+list.map(r=>`<li class="click" data-evt="${esc(r.e.id)}"><span class="nl-when">${esc(fmtAt(r.at))}</span><span class="nl-what">${what(r)}</span><span class="nl-ic ${r.kind}">${r.kind==='prep'?IC.doc:IC.clock}</span></li>`).join('')+'</ul>'
      :'<p class="muted">'+t('n.noneUpcoming')+'</p>'}
    ${st==='denied'?'<p class="tiny mt">'+t('n.denied')+'</p>':''}
  </div>`;
}

/* ---------------- settings ---------------- */
function deviceBlock(){
  if(!loggedIn())return '<div class="ndevice"><span class="ndot"></span><div class="grow"><div class="nd-v">'+t('n.needLogin')+'</div></div></div>';
  const st=deviceState();
  const msg={unknown:'…',on:t('n.on'),off:t('n.off'),denied:t('n.denied'),unsupported:t('n.unsupported'),'ios-install':t('n.iosInstall')}[st];
  const btn=st==='on'?`<button class="btn small ghost" data-push="off">${t('n.disable')}</button>`
    :st==='off'?`<button class="btn small primary" data-push="on" ${PUSH.busy?'disabled':''}>${t('n.enable')}</button>`
    :st==='ios-install'?`<button class="btn small primary" data-inst="go">${t('inst.btn')}</button>`:'';
  return `<div class="ndevice ${st}"><span class="ndot"></span><div class="grow"><div class="nd-k">${t('n.device')}</div><div class="nd-v">${msg}</div></div>${btn?'<div class="row" style="gap:6px">'+btn+'</div>':''}</div>`;
}
function tzList(cur){
  let all=[];try{all=Intl.supportedValuesOf('timeZone')}catch(e){}
  if(!all.length)all=['Europe/Berlin','Europe/Vienna','Europe/Zurich','Europe/Paris','Europe/Brussels','Europe/Amsterdam','Europe/London','Europe/Madrid','Europe/Rome','UTC'];
  if(cur&&!all.includes(cur))all=[cur,...all];
  return all.map(z=>'<option value="'+esc(z)+'"'+(z===cur?' selected':'')+'>'+esc(z.replace(/_/g,' '))+'</option>').join('');
}
export function settingsRemindersCard(){
  const p=nprefs();const sr={on:false,hours:24,...(S.settings.stuRemind||{})};
  const ck=k=>`<input type="checkbox" data-n="${k}" ${p[k]?'checked':''}>`;
  const opts=(vals,cur,lab)=>vals.map(v=>'<option value="'+v+'"'+(+cur===v?' selected':'')+'>'+lab(v)+'</option>').join('');
  const row=(k,title,hint,ctrl)=>`<div class="nrow"><label class="check">${ck(k)}<span><b>${title}</b>${hint?'<small>'+hint+'</small>':''}</span></label>${ctrl||''}</div>`;
  const emailNote=PUSH.cfgState==='missing'?'<p class="nnote">'+t('n.notReady')+'</p>'
    :PUSH.cfg&&PUSH.cfg.email===false?'<p class="nnote">'+t('n.emailOff')+'</p>':'';
  const on=deviceState()==='on'||(p.email&&PUSH.cfg&&PUSH.cfg.email);
  return `<div class="card mb" id="reminders">
    <div class="cardhead"><h2>${t('n.title')}</h2>${loggedIn()?'<span class="chip '+(on?'ok':'')+'">'+(on?t('n.chipOn'):t('n.chipOff'))+'</span>':''}</div>
    ${deviceBlock()}
    <div class="nsub">${t('n.when')}</div>
    ${row('prep',t('n.prep'),t('n.prepHint'),'<select class="select sm" data-n="prepDays" aria-label="'+t('n.prep')+'">'+opts([1,2,3,4,5,7],p.prepDays,v=>v===1?t('n.day1'):t('n.daysN',{n:v}))+'</select>')}
    ${row('soon',t('n.soon'),t('n.soonHint'),'<select class="select sm" data-n="soonMin" aria-label="'+t('n.soon')+'">'+opts([15,30,45,60,90,120],p.soonMin,v=>t('n.minBefore',{n:v}))+'</select>')}
    ${row('after',t('n.after'),t('n.afterHint'))}
    ${row('agenda',t('n.agenda'),t('n.agendaHint'),'<input class="input sm" type="time" data-n="agendaAt" value="'+esc(p.agendaAt)+'" aria-label="'+t('n.agenda')+'">')}
    <div class="nsub">${t('n.how')}</div>
    ${row('push',t('n.chPush'),'')}
    ${row('email',t('n.chEmail',{e:esc((CLOUD.auth&&CLOUD.auth.email)||'')}),t('n.emailHint'))}
    ${emailNote}
    ${row('quiet',t('n.quiet'),t('n.quietHint'),'<span class="row nquiet"><input class="input sm" type="time" data-n="quietFrom" value="'+esc(p.quietFrom)+'" aria-label="'+t('n.quiet')+'"><span>–</span><input class="input sm" type="time" data-n="quietTo" value="'+esc(p.quietTo)+'" aria-label="'+t('n.quiet')+'"></span>')}
    <div class="nrow"><span class="nlabel">${t('n.tz')}</span><select class="select sm" id="se-tz">${tzList(S.settings.tz)}</select></div>
    <div class="nsub">${t('n.stuTitle')}</div>
    <div class="nrow"><label class="check"><input type="checkbox" id="se-stur" ${sr.on?'checked':''}><span><b>${t('n.stu')}</b><small>${t('n.stuHint')}</small></span></label>
      <select class="select sm" id="se-sturh" aria-label="${t('n.stu')}">${opts([2,6,12,24,48],sr.hours,v=>t('n.hBefore',{n:v}))}</select></div>
  </div>`;
}
export function settingsFeedCard(){
  return `<div class="card mb"><div class="cardhead"><h2>${t('feed.title')}</h2></div>
    <p class="muted mb8" style="font-size:13px">${t('feed.body')}</p>
    <button class="btn small primary" data-feed="open" ${loggedIn()?'':'disabled'}>${IC.cal} ${t('feed.open')}</button></div>`;
}

/* ---------------- bindings (all views) ---------------- */
export function bindReminders(m){
  m.querySelectorAll('[data-push]').forEach(el=>el.onclick=()=>{
    const a=el.dataset.push;if(a==='on')enablePush();else if(a==='off')disablePush();
  });
  m.querySelectorAll('[data-inst]').forEach(el=>el.onclick=()=>installApp());
  m.querySelectorAll('[data-feed]').forEach(el=>el.onclick=()=>openFeedSheet());
  m.querySelectorAll('img[data-qr]').forEach(img=>import('qrcode').then(Q=>(Q.default||Q).toDataURL(img.dataset.qr,{margin:1,width:264,color:{dark:'#1B1C22',light:'#FFFFFF'}})).then(u=>{img.src=u}).catch(()=>img.remove()));
  m.querySelectorAll('[data-n]').forEach(el=>el.onchange=()=>{
    const k=el.dataset.n;const n={...nprefs()};
    n[k]=el.type==='checkbox'?el.checked:el.tagName==='SELECT'?+el.value:el.value;
    S.settings.notify=n;persist();
  });
  const tz=m.querySelector('#se-tz');if(tz)tz.onchange=()=>{S.settings.tz=tz.value;persist()};
  const sr=m.querySelector('#se-stur'),srh=m.querySelector('#se-sturh');
  const saveSr=()=>{S.settings.stuRemind={on:sr.checked,hours:+srh.value||24};persist()};
  if(sr)sr.onchange=saveSr;if(srh)srh.onchange=saveSr;
  if(RT.v==='settings'&&loggedIn()&&PUSH.cfgState==='idle'){
    PUSH.cfgState='loading';
    pushConfig().catch(()=>{}).finally(()=>{if(RT.v==='settings'&&!document.querySelector('.sheetback'))render()});
  }
}
